import { useEffect, useRef } from "react";
import { Check, User, ChevronLeft, ChevronRight, FileText, MessageCircle } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";

import "swiper/swiper.min.css";
import "swiper/components/navigation/navigation.min.css";
import "swiper/components/pagination/pagination.min.css";

SwiperCore.use([Navigation, Pagination, Autoplay]);

const AddAppointmentContentStep1 = ({ services, staff, onComplete, selectedService,
selectedType,
selectedMethod,
selectedStaff,
setSelectedService,
setSelectedType,
setSelectedMethod,
isComplete,
setSelectedStaff,
  showRules,
  setShowRules,
}) => {

  const typeSectionRef = useRef(null);
  const methodSectionRef = useRef(null);
  const staffSectionRef = useRef(null);
  const bottomRef = useRef(null);

   // اسکرول خودکار وقتی خدمت انتخاب می‌شه → برو به بخش نوع خدمت
  useEffect(() => {
    if (selectedService && typeSectionRef.current) {
      typeSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedService]);

  // وقتی نوع خدمت انتخاب می‌شه → برو به بخش متد
  useEffect(() => {
    if (selectedType && methodSectionRef.current) {
      methodSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedType]);

  // وقتی متد انتخاب می‌شه:
  // اگه VIP باشه و هنوز نیرو انتخاب نشده → برو به بخش نیروها
  // اگه عادی باشه → برو به پایین (دکمه ادامه)
  useEffect(() => {
    if (!selectedMethod) return;
    if (selectedMethod === "vip" && !selectedStaff && staffSectionRef.current) {
      staffSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (selectedMethod !== "vip" && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedMethod, selectedStaff]);

  // وقتی نیرو انتخاب می‌شه (در VIP) → برو به پایین
  useEffect(() => {
    if (selectedStaff && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedStaff]);


  // توابع کمکی دقیقاً مشابه AppointmentModal
  const getActiveTypes = () => {
    if (!selectedService) return [];
    const types = [];
    if (selectedService.kasht) types.push({ key: "kasht", label: "کاشت" });
    if (selectedService.retoosh) types.push({ key: "retoosh", label: "رتوش" });
    if (selectedService.tarmim) types.push({ key: "tarmim", label: "ترمیم" });
    if (selectedService.lift) types.push({ key: "lift", label: "لیفت" });
    return types;
  };

  const getActiveMethods = () => {
    if (!selectedService) return [];
    const methods = [];
    if (selectedService.normal?.enabled)
      methods.push({
        key: "عادی",
        label: "عادی",
        price: selectedService.normal.price,
      });
    if (selectedService.vip?.enabled)
      methods.push({
        key: "vip",
        label: "VIP",
        price: selectedService.vip.price,
      });
    return methods;
  };

  const getEligibleStaff = () => {
    if (!selectedService || !selectedType || !selectedMethod) return [];
    return staff.filter((member) => {
      const serviceEntry = member.services?.find(
        (s) => s.serviceId === selectedService._id
      );
      if (!serviceEntry) return false;
      if (!serviceEntry[selectedType]) return false;
      if (selectedMethod === "vip") return serviceEntry.vip?.enabled === true;
      if (selectedMethod === "عادی") return serviceEntry.normal?.enabled === true;
      return false;
    });
  };

  const activeTypes = getActiveTypes();
  const activeMethods = getActiveMethods();
  const eligibleStaff = selectedMethod === "vip" ? getEligibleStaff() : [];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 max-w-6xl mx-auto">
      {/* مرحله ۱: انتخاب خدمت */}
      <div className="mb-8">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
              1
            </span>
            خدمت خود را انتخاب کنید
          </h3>
          <p className="text-sm text-slate-500 mr-10">
            یکی از خدمات زیر را برای رزرو انتخاب کنید
          </p>
        </div>

        {services.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <span className="text-5xl mb-3">📷</span>
            <p className="text-sm">هیچ خدمتی یافت نشد</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {services.map((service) => (
              <div
                key={service._id}
                onClick={() => {
                  setSelectedService(service);
                  setSelectedType(null);
                  setSelectedMethod(null);
                  setSelectedStaff(null);
                }}
                className={`
                  relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-300
                  ${
                    selectedService?._id === service._id
                      ? "border-purple-500 shadow-lg shadow-purple-500/30 scale-105"
                      : "border-slate-200 hover:border-purple-300 hover:shadow-md"
                  }
                `}
              >
                <div className="relative h-40 bg-slate-100">
                  {service.images && service.images.length > 0 ? (
                    <>
                      <Swiper
                        spaceBetween={0}
                        loop={service.images.length > 1}
                        slidesPerView={1}
                        autoplay={{
                          delay: 2000,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }}
                        navigation={{
                          nextEl: `.next-${service._id}`,
                          prevEl: `.prev-${service._id}`,
                        }}
                        className="h-full"
                      >
                        {service.images.map((img, idx) => (
                          <SwiperSlide key={idx}>
                            <img
                              src={
                                img?.includes("https://") || img?.includes("http://")
                                  ? img
                                  : "https://my-sarad-back.liara.run" + img
                              }
                              alt={service.name}
                              className="w-full h-full object-cover"
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <button
                        className={`prev-${service._id} absolute right-2 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white`}
                      >
                        <ChevronRight size={26} />
                      </button>
                      <button
                        className={`next-${service._id} absolute left-2 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white`}
                      >
                        <ChevronLeft size={26} />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <span className="text-4xl">📷</span>
                    </div>
                  )}
                  {selectedService?._id === service._id && (
                    <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-3 bg-white">
                  <p className="text-sm font-semibold text-slate-800 text-center line-clamp-2">
                    {service.name}
                  </p>
                  {service.kasht && service.kashtPrice > 0 && (
                    <p className="text-center text-[9px] sm:text-[10px] text-slate-500 leading-tight whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                      قیمت کاشت:
                      <span className="mr-0.5 font-bold text-purple-600">
                        {((service.kashtPrice || 0)).toLocaleString('fa-IR')}
                        <span className="text-[8px] text-gray-400 mr-1">تومان</span>
                      </span>
                    </p>
                  )}
                  {service.retoosh && service.retooshPrice > 0 && (
                    <p className="text-center text-[9px] sm:text-[10px] text-slate-500 leading-tight whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                      قیمت روتوش:
                      <span className="mr-0.5 font-bold text-purple-600">
                        {((service.retooshPrice || 0)).toLocaleString('fa-IR')}
                        <span className="text-[8px] text-gray-400 mr-1">تومان</span>
                      </span>
                    </p>
                  )}
                  {service.tarmim && service.tarmimPrice > 0 && (
                    <p className="text-center text-[9px] sm:text-[10px] text-slate-500 leading-tight whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                      قیمت ترمیم:
                      <span className="mr-0.5 font-bold text-purple-600">
                        {((service.tarmimPrice || 0)).toLocaleString('fa-IR')}
                        <span className="text-[8px] text-gray-400 mr-1">تومان</span>
                      </span>
                    </p>
                  )}
                  {service.lift && service.liftPrice > 0 && (
                    <p className="text-center text-[9px] sm:text-[10px] text-slate-500 leading-tight whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                      قیمت لیفت:
                      <span className="mr-0.5 font-bold text-purple-600">
                        {((service.liftPrice || 0)).toLocaleString('fa-IR')}
                        <span className="text-[8px] text-gray-400 mr-1">تومان</span>
                      </span>
                    </p>
                  )}
                </div>
                <div className="absolute top-2 right-2">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      selectedService?._id === service._id
                        ? "border-purple-600 bg-purple-600"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {selectedService?._id === service._id && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* قوانین و مقررات + رزرو وقت لاینB */}
      {selectedService && (
        <div className="mb-7 grid grid-cols-1 md:grid-cols-2 gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* رزرو وقت لاینB */}
          <button
            onClick={() => {
              window.open("https://wa.me/989006167070", "_blank");
            }}
            className="group w-full rounded-2xl border border-slate-200 bg-white p-4 text-right shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-200"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600 transition-colors group-hover:bg-green-100">
                <MessageCircle className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-800">رزرو وقت لاین B</p>
                <p className="mt-1 text-xs font-medium text-slate-400" dir="rtl">واتساپ · ۰۹۰۰۶۱۶۷۰۷۰</p>
              </div>
              <ChevronLeft className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:-translate-x-0.5 group-hover:text-purple-500" />
            </div>
          </button>

          {/* قوانین و مقررات */}
          <button
            onClick={() => setShowRules(true)}
            className="group w-full rounded-2xl border border-purple-100 bg-gradient-to-l from-purple-50/80 to-white p-4 text-right shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-200"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-100 text-purple-700 transition-colors group-hover:bg-purple-200">
                <FileText className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-800">قوانین و مقررات رزرو</p>
                <p className="mt-1 text-xs font-medium text-purple-500">مشاهده جزئیات پیش از پرداخت</p>
              </div>
              <ChevronLeft className="h-4 w-4 shrink-0 text-purple-300 transition-transform group-hover:-translate-x-0.5 group-hover:text-purple-600" />
            </div>
          </button>
        </div>
      )}

      {/* مودال قوانین و مقررات */}
      {showRules && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowRules(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5 animate-in fade-in zoom-in duration-300 border border-purple-100" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
                  📜
                </span>
                <h3 className="text-lg font-bold text-slate-800">قوانین و مقررات رزرو</h3>
              </div>
              <button onClick={() => setShowRules(false)} className="w-8 h-8 bg-purple-50 hover:bg-purple-100 rounded-full flex items-center justify-center text-purple-400 hover:text-purple-600 transition-all">
                ×
              </button>
            </div>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed" dir="rtl">
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <p className="font-semibold text-purple-800 mb-1">۱. امکان تغییر رزرو</p>
                <p className="text-slate-600 text-xs">تا ۲۴ ساعت قبل از نوبت مقرر امکان‌پذیر است.</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="font-semibold text-blue-800 mb-1">۲. محدودیت رزرو</p>
                <p className="text-slate-600 text-xs">هر مشتری در بازه زمانی ۷ روزه فقط می‌تواند یک نوبت رزرو داشته باشد.</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="font-semibold text-red-800 mb-1">۳. عدم حضور</p>
                <p className="text-slate-600 text-xs">در صورت عدم حضور در زمان مقرر، رزرو لغو خواهد شد.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="font-semibold text-green-800 mb-1">۴. تأیید رزرو</p>
                <p className="text-slate-600 text-xs">رزرو با تأیید پرداخت نهایی می‌شود.</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="font-semibold text-amber-800 mb-1">۵. لغو و بازپرداخت</p>
                <p className="text-slate-600 text-xs">در صورت لغو توسط مشتری، بازپرداخت طبق قوانین انجام می‌شود.</p>
              </div>
              <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                <p className="font-semibold text-teal-800 mb-1">۶. تغییر مژه‌کار</p>
                <p className="text-slate-600 text-xs">تغییر نام مژه‌کار ممکن است بسته به ظرفیت باشد.</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                <p className="font-semibold text-indigo-800 mb-1">۷. تغییر نوع خدمت</p>
                <p className="text-slate-600 text-xs">تغییر از VIP به عادی و برعکس تا ۲۴ ساعت قبل از نوبت امکان‌پذیر است.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* مرحله ۲: انتخاب نوع خدمت (کاشت/رتوش/...) */}
      {selectedService && activeTypes.length > 0 && (
        <div ref={typeSectionRef} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
                2
              </span>
              یکی از این گزینه‌ها را انتخاب کنید
            </h3>
            <p className="text-sm text-slate-500 mr-10">
              نوع خدمت مورد نظر خود را مشخص کنید
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {activeTypes.map((type) => (
              <div
                key={type.key}
                onClick={() => {
                  setSelectedType(type.key);
                  setSelectedMethod(null);
                  setSelectedStaff(null);
                }}
                className={`
                  relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-300
                  ${
                    selectedType === type.key
                      ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                      : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }
                `}
              >
                <div className="text-center">
                  <p className="text-base font-bold text-slate-800 mb-2">
                    {type.label}
                  </p>
                  <div className="flex justify-center mt-3">
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                        selectedType === type.key
                          ? "border-purple-600 bg-purple-600"
                          : "border-slate-300 bg-white"
                      }`}
                    >
                      {selectedType === type.key && (
                        <div className="w-3 h-3 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                </div>
                {selectedType === type.key && (
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* مرحله ۳: انتخاب شیوه (عادی/VIP) */}
      {/* {selectedService && selectedType && activeMethods.length > 0 && (
        <div ref={methodSectionRef} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
                3
              </span>
              شیوه‌ی انجام خدمت را انتخاب کنید
            </h3>
            <p className="text-sm text-slate-500 mr-10">
              حالت عادی یا VIP را مشخص کنید
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            {activeMethods.map((method) => (
              <div
                key={method.key}
                onClick={() => {
                  setSelectedMethod(method.key);
                  setSelectedStaff(null);
                }}
                className={`
                  relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-300
                  ${
                    selectedMethod === method.key
                      ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                      : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-slate-800 mb-1">
                      {method.label}
                    </p>
                    <p className="text-sm text-slate-500">
                      پیش پرداخت:{" "}
                      <span className="font-semibold text-purple-600">
                        500,000 تومان
                      </span>
                    </p>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      selectedMethod === method.key
                        ? "border-purple-600 bg-purple-600"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {selectedMethod === method.key && (
                      <div className="w-3 h-3 rounded-full bg-white" />
                    )}
                  </div>
                </div>
                {selectedMethod === method.key && (
                  <div className="absolute top-1/2 -translate-y-1/2 left-6 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )} */}
      {selectedService && selectedType && activeMethods.length > 0 && (
        <div ref={methodSectionRef} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
                3
              </span>
              شیوه‌ی انجام خدمت را انتخاب کنید
            </h3>
            <p className="text-sm text-slate-500 mr-10">
              حالت عادی یا VIP را مشخص کنید
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            {activeMethods.map((method) => (
              <div
                key={method.key}
                onClick={() => {
                  setSelectedMethod(method.key);
                  setSelectedStaff(null);
                }}
                className={`
                  relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-300
                  ${
                    selectedMethod === method.key
                      ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                      : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-slate-800 mb-1">
                      {method.label}
                    </p>
                    <p className="text-sm text-slate-500">
                      پیش پرداخت:{" "}
                      <span className="font-semibold text-purple-600">
                        500,000 تومان
                      </span>
                    </p>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      selectedMethod === method.key
                        ? "border-purple-600 bg-purple-600"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {selectedMethod === method.key && (
                      <div className="w-3 h-3 rounded-full bg-white" />
                    )}
                  </div>
                </div>
                {selectedMethod === method.key && (
                  <div className="absolute top-1/2 -translate-y-1/2 left-6 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
          {selectedMethod === "vip" && (
            <div className="mt-4 max-w-2xl px-4 py-3 rounded-xl bg-red-50 border border-red-200">
              <p className="text-sm text-red-600 font-medium">
                ⚠️ به کلیه خدمات VIP مبلغ ۵۵۰٬۰۰۰ تومان اضافه می‌شود.
              </p>
            </div>
          )}
        </div>
      )}

      {/* مرحله ۴: انتخاب مژه‌کار (فقط برای VIP) */}
      {selectedService && selectedType && selectedMethod === "vip" && (
        <div ref={staffSectionRef} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
                4
              </span>
              مژه‌کار مورد نظر را انتخاب کنید
            </h3>
            <p className="text-sm text-slate-500 mr-10">
              مژه‌کاری که مایلید با آن تایم رزرو کنید را انتخاب کنید
            </p>
          </div>

          {eligibleStaff.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <span className="text-5xl mb-3">👤</span>
              <p className="text-sm">هیچ کارکنی برای این خدمت یافت نشد</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {eligibleStaff.map((member) => (
                <div
                  key={member._id}
                  onClick={() => setSelectedStaff(member)}
                  className={`
                    relative cursor-pointer rounded-xl p-4 border-2 transition-all duration-300 flex items-center gap-3
                    ${
                      selectedStaff?._id === member._id
                        ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                        : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"
                    }
                  `}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      selectedStaff?._id === member._id
                        ? "bg-purple-600 text-white"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {member.information?.firstName?.charAt(0) ||
                      member.information?.displayName?.charAt(0) || (
                        <User className="w-4 h-4" />
                      )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {member.information?.firstName
                        ? `${member.information.firstName} ${member.information.lastName}`
                        : member.information?.displayName}
                    </p>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
                      selectedStaff?._id === member._id
                        ? "border-purple-600 bg-purple-600"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {selectedStaff?._id === member._id && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default AddAppointmentContentStep1;
